import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

export function formatDist(m) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

// Opt-in per-cluster step, shared by PhotoJournalReviewModal.js and
// PersonalizeGuideReviewModal.js's new-place rows: looks up nearby named
// real-world places (Wikipedia geosearch, free) plus the resolved city
// (Google reverse geocode) for a cluster's cover-photo coordinates, and lets
// the user pick which one the photos actually show — the opposite direction
// of "Personalize Guide with Photos" matching photos to already-known
// places; this creates a new place's identity FROM photos.
// "Pick on the map" (4.10.55): offered with or without a search, for a place
// that isn't in the list but is on Google Maps (onPickOnMap opens the map).
// onCancel (optional, the place editor): closes the list and undoes the pick.
const mapBtnStyle = {
  marginTop: 6, marginLeft: 6, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff",
  color: "#374151", fontSize: 11, padding: "3px 8px", cursor: "pointer",
};

export default function IdentifyPlace({ state, onRun, onFindCandidates, onPick, onPickOnMap, onCancel }) {
  const mapButton = onPickOnMap ? (
    <button className="identify-pick-map" onClick={onPickOnMap} style={mapBtnStyle}>
      📍 Pick on the map
    </button>
  ) : null;
  const cancelButton = onCancel ? (
    <button className="identify-cancel" onClick={onCancel} style={mapBtnStyle}>
      Cancel
    </button>
  ) : null;
  if (!state) {
    return (
      <div>
      <button
        onClick={onRun}
        style={{
          marginTop: 6, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff",
          color: "#374151", fontSize: 11, padding: "3px 8px", cursor: "pointer",
        }}
      >
        🔎 Identify this place
      </button>
      {mapButton}
      </div>
    );
  }
  if (state.status === "loading") {
    return (
      <div style={{ marginTop: 6, fontSize: 11, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
        <FontAwesomeIcon icon={solid("spinner")} spin />
        Looking for nearby places…
      </div>
    );
  }
  const { city, candidates, pickedTitle } = state;
  // candidates === null means bulk identify resolved the city but
  // deliberately skipped the Wikipedia landmark search for speed (see
  // PhotoJournalReviewModal.js's runIdentify comment) — distinct from
  // candidates === [] (searched, genuinely found nothing nearby). Only the
  // latter should read as "no named places found."
  const candidatesSkipped = candidates === null;
  // A name picked on the map isn't one of the rows below — show it.
  const fromMap = pickedTitle && pickedTitle !== city && !(candidates || []).some((c) => c.title === pickedTitle) ? pickedTitle : null;
  if (!candidatesSkipped && candidates.length === 0 && !city && !fromMap) {
    return (
      <div style={{ marginTop: 6, fontSize: 11, color: "#6b7280" }}>
        No named places found nearby.
        {mapButton}
        {cancelButton}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
      <div
        onClick={() => onPick(null)}
        style={{
          fontSize: 11, cursor: "pointer", padding: "2px 6px", borderRadius: 4,
          background: pickedTitle === null ? "#eef2ff" : "transparent",
          color: pickedTitle === null ? "#3730a3" : "#6b7280",
        }}
      >
        {pickedTitle === null ? "● " : "○ "}None of these{city ? ` (name it "${city}_N")` : ""}
      </div>
      {candidatesSkipped && onFindCandidates && (
        <button
          onClick={onFindCandidates}
          style={{
            alignSelf: "flex-start", marginTop: 2, border: "1px solid #d1d5db", borderRadius: 6,
            background: "#fff", color: "#374151", fontSize: 11, padding: "2px 8px", cursor: "pointer",
          }}
        >
          🔎 Find a specific landmark name
        </button>
      )}
      {city && (
        // Wikipedia geosearch can miss a real place entirely — small
        // hamlets/frazioni (e.g. Panzano) often have no article of their
        // own, only a redirect into their parent town's page with no
        // coordinates under the original name, so they can never appear in
        // the list below no matter how the search is tuned. The city name
        // itself comes from Google's reverse geocoding (a much denser
        // database), so offering it as a direct, nameable pick — not just
        // the disambiguated "_N" fallback used when nothing is picked at
        // all — covers exactly that gap.
        <div
          onClick={() => onPick(city)}
          style={{
            fontSize: 11, cursor: "pointer", padding: "2px 6px", borderRadius: 4,
            background: pickedTitle === city ? "#eef2ff" : "transparent",
            color: pickedTitle === city ? "#3730a3" : "#374151",
          }}
        >
          {pickedTitle === city ? "● " : "○ "}📍 Use city name: {city}
        </div>
      )}
      {fromMap ? (
        <div className="identify-from-map" style={{ fontSize: 11, padding: "2px 6px", borderRadius: 4, background: "#eef2ff", color: "#3730a3" }}>
          ● 📍 From the map: {fromMap}
        </div>
      ) : null}
      {(candidates || []).map((c) => (
        <div
          key={c.pageid}
          onClick={() => onPick(c.title)}
          style={{
            fontSize: 11, cursor: "pointer", padding: "2px 6px", borderRadius: 4,
            display: "flex", alignItems: "center", gap: 6,
            background: pickedTitle === c.title ? "#eef2ff" : "transparent",
            color: pickedTitle === c.title ? "#3730a3" : "#374151",
          }}
        >
          <span style={{ width: 12, textAlign: "center", flexShrink: 0 }}>{pickedTitle === c.title ? "●" : "○"}</span>
          {c.thumbnail && (
            <img src={c.thumbnail} alt="" style={{ width: 20, height: 20, borderRadius: 3, objectFit: "cover", flexShrink: 0 }} />
          )}
          <span>
            {c.title}
            <span style={{ color: "#9ca3af" }}> — {formatDist(c.dist)}</span>
            {c.description ? <span style={{ color: "#9ca3af" }}> · {c.description}</span> : null}
          </span>
        </div>
      ))}
      {mapButton || cancelButton ? (
        <div>
          {mapButton}
          {cancelButton}
        </div>
      ) : null}
    </div>
  );
}
